import { Fragment, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Control, Controller } from 'react-hook-form';
import FormLabel from './FormLabel';
import ErrorMessage from './ErrorMessage';
import { classNames } from '@/utils/common.utils';

type CustomSelectProps = {
	name: string;
	label?: string;
	options: Option[];
	defaultValue?: Option;
	control: Control<any>;
	errorMessage: string | undefined;
	reset?: boolean;
};

type Option = { value: string | number | boolean | undefined; label: string; color?: string };

export default function CustomSelect({
	name,
	label,
	options,
	defaultValue,
	control,
	errorMessage,
	reset,
}: CustomSelectProps) {
	const [selected, setSelected] = useState<Option | null>(defaultValue || null);

	useEffect(() => {
		if (reset) setSelected(defaultValue || null);
	}, [reset]);

	const handleSelectChange = (option: Option, onChange: (value: string | number | boolean | undefined) => void) => {
		setSelected(option);
		onChange(option.value); // Update the form field value
	};

	return (
		<Controller
			control={control}
			name={name}
			render={({ field }) => (
				<Listbox
					{...field}
					value={selected}
					onChange={(option) => {
						if (!!option) handleSelectChange(option, field.onChange);
					}}
				>
					{({ open }) => (
						<div className="relative">
							{label && <FormLabel labelFor={name} labelName={label} />}
							<Listbox.Button
								className={`relative mt-2 h-9 w-full cursor-default pl-3 text-left ${
									errorMessage ? 'app-input-error' : 'app-input-default'
								}`}
							>
								<div className="flex items-center gap-x-2">
									{selected?.color && (
										<span
											className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
											style={{ backgroundColor: selected.color }}
											aria-hidden="true"
										/>
									)}
									{selected?.value === 'placeholder' ? (
										<span className="block truncate text-ring">{selected?.label}</span>
									) : (
										<span className="block truncate">
											{selected?.value !== undefined ? selected?.label : ''}
										</span>
									)}
								</div>
								<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
									<ChevronUpDownIcon
										className={`h-5 w-5 ${
											errorMessage ? 'text-destructive' : 'text-muted-foreground/70'
										}`}
										aria-hidden="true"
									/>
								</span>
							</Listbox.Button>

							<Transition
								show={open}
								as={Fragment}
								leave="transition ease-in duration-100"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-background py-1 text-base shadow-lg ring-1 ring-ring ring-opacity-5 focus:outline-none sm:text-sm">
									{options
										.filter((option) => option.value !== 'placeholder')
										.map((option, index) => (
											<Listbox.Option
												key={index}
												className={({ active }) =>
													classNames(
														active
															? 'bg-primary text-primary-foreground'
															: 'text-foreground',
														'relative cursor-default select-none py-2 pl-3 pr-9',
													)
												}
												value={option}
											>
												{({ selected, active }) => (
													<>
														<div className="flex items-center gap-x-2">
															{option.color && (
																<span
																	className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
																	style={{ backgroundColor: option.color }}
																	aria-hidden="true"
																/>
															)}
															<span
																className={classNames(
																	'text-sm',
																	selected ? 'font-normal' : 'font-normal',
																	'block truncate',
																)}
															>
																{option.label}
															</span>
														</div>

														{selected ? (
															<span
																className={classNames(
																	active ? 'text-primary-foreground' : 'text-primary',
																	'absolute inset-y-0 right-0 flex items-center pr-4',
																)}
															>
																<CheckIcon className="h-5 w-5" aria-hidden="true" />
															</span>
														) : null}
													</>
												)}
											</Listbox.Option>
										))}
								</Listbox.Options>
							</Transition>
							{!!errorMessage && <ErrorMessage inputId={name} errorMessage={errorMessage} />}
						</div>
					)}
				</Listbox>
			)}
		/>
	);
}
